/*
 * Project: BookingEngine
 * Module: components/booking/hotels/Hotel.js
 * @flow
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import formatCurrency from 'format-currency';
import { formatDate } from 'bookingform';

import PictureSlider from '../rooms/PictureSlider';
import CalendarIcon from '../../icons/CalendarIcon';

import { getLangValue, getDistanceText } from '../../../utils/Generic';
import { DEVICES, setAvailabilityHotel } from '../../../actions/Booking';
import { getCheapestHotelOption } from '../../../utils/Room';

import '../../../static/less/hotel/hotel.less';

//import type { State } from '../../../FlowTypes';
import { MULTIPLE_ROOMS } from '../../../constants';
import withNavigate from '../../../withNavigate'; // Importa el HOC

class Hotel extends React.Component {
  selectHotel = () => {
    this.props.dispatch(setAvailabilityHotel(this.props.hotel));
    this.props.navigate('/rooms/');
  };

  getTwoRoomItem(hotelOption, roomId) {
    const rooms = this.props.hotel.rooms[roomId];
    return rooms.find((r) => r.code === hotelOption.bestRoomCodes[roomId]);
  }

  getRate(hotelOption) {
    let rates;
    if (hotelOption.singleRoom) {
      rates = hotelOption.singleRoom.rates;
    } else {
      rates = this.getTwoRoomItem(hotelOption, MULTIPLE_ROOMS.FIRST).rates;
    }
    return rates.find((r) => r.code === hotelOption.rateCode);
  }

  getBoard(hotelOption, rate) {
    return rate.boards.find((b) => b.code === hotelOption.boardCode);
  }

  getTwoRoomName(hotelOption) {
    const room1 = this.getTwoRoomItem(hotelOption, MULTIPLE_ROOMS.FIRST);
    const room2 = this.getTwoRoomItem(hotelOption, MULTIPLE_ROOMS.SECOND);

    const name1 = getLangValue(room1, this.props.lang, 'name');
    const name2 = getLangValue(room2, this.props.lang, 'name');
    // // Case for 2 rooms: show both names only when they have different names
    return (name1 === name2) ? name1 : `${name1} / ${name2}`;
  }

  getRoomName(hotelOption) {
    // Returns the localised value of the (room / rate / board), for one or two rooms
    const isSingleRoom = !!hotelOption.singleRoom;
    if (isSingleRoom) {
      return getLangValue(hotelOption.singleRoom, this.props.lang, 'name');
    }
    return this.getTwoRoomName(hotelOption);
  }

  renderAvailableMobileRate(minPriceFrom, currency) {
    return (
      <div className="MobileBookItem">
        <div className="HotelName">{this.props.hotel.name}</div>
        <div className="PriceFrom">
          <span>{this.context.t('From')}&nbsp;</span>
          {formatCurrency(minPriceFrom, currency)}
        </div>
        <div className="BookBtn">
          <span>{this.context.t('Book now')}</span>
        </div>
      </div>
    );
  }

  renderAvailableRate() {
    const { hotel, lang } = this.props;
    const { checkin, checkout, nights } = this.props.info;
    const currency = { ...this.props.currency, locale: lang };

    const cheapestOption = getCheapestHotelOption(hotel, currency.code);
    if (!cheapestOption) {
      return null;
    }
    const rate = this.getRate(cheapestOption);
    const board = this.getBoard(cheapestOption, rate);
    return (
      <div className="HotelAvailable">
        <div className="HotelAvailable__header">
          <b>{this.getRoomName(cheapestOption)}</b>
          <span>{getLangValue(rate, lang, 'name')}</span>
        </div>
        <div className="HotelAvailableDates">
          <b>
            {this.context.t(['{n} night', '{n} nights', 'n'], { n: nights })}
            &nbsp;-&nbsp;
            {getLangValue(board, lang, 'name')}
            &nbsp;-&nbsp;
          </b>
          <span>
            <CalendarIcon />
            {formatDate(checkin, lang)}
            &nbsp;-&nbsp;
            {formatDate(checkout, lang)}
          </span>
        </div>
        <div className="HotelAvailableButton" onClick={this.selectHotel}>
          <div className="ButtonItem MinPrice">
            <span>{this.context.t('From')}</span>
            {formatCurrency(cheapestOption.bestPrice, currency)}
          </div>
          <div className="ButtonItem BookBtn d-none">
            <span>{this.context.t('Book now')}</span>
            <i className="fa fa-angle-right" />
          </div>
          {this.renderAvailableMobileRate(cheapestOption.bestPrice, currency)}
        </div>
      </div>
    );
  }

  renderNotAvailable() {
    return (
      <div className="HotelNotAvailable">
        <div>
          <b>{this.context.t('No availability with the details you selected')}</b>
          <p>{this.context.t('Sorry, but there is no availability for the hotel and dates you selected.')}</p>
          <p>{this.props.hotel.reason_not_aviable === 'NotStay' ? this.context.t('Minimum stay {n} nights', { n: this.props.hotel.min_stays }) : ''}</p>
        </div>
      </div>
    );
  }

  renderHotelName() {
    const { name, city, segment } = this.props.hotel;
    return (
      <div className="HotelLabel">
        <b>{name}</b>
        {city.name}
        &nbsp;/&nbsp;
        <i className={segment.icon} />
        {segment.full_name}
      </div>
    );
  }

  renderDistance() {
    const { distance } = this.props.hotel;
    if (!distance) {
      return null;
    }

    return (
      <div className="HotelDistance text-base">
        <i className="fa fa-arrows-h"/>
        {this.context.t('Distance')}: <span className='font-bold'>{getDistanceText(distance)}</span>
      </div>
    );
  }

  renderHotelDetails() {
    const { hotel } = this.props;
    const { segment } = hotel;
    return (
      <div className="HotelDetails">
        <b>{hotel.name}</b>
        <small className="font-bold primary_text_color">{hotel.city.name} - {hotel.city.zone.name}</small>
        <strong className="text-highlight">
          <i className={segment.icon}/>
          {segment.full_name}
        </strong>
        <p>{getLangValue(hotel, this.props.lang, 'description')}</p>
      </div>
    );
  }

  render() {
    const { hotel, isMobile } = this.props;
    const imageSize = isMobile ? 'xs' : 'sm';
    const photo = hotel.photos[0];
    return (
      <div className="Hotel col-sm-12">
        {/* eslint-disable-next-line react/no-string-refs */}
        <div className="HotelPhoto" ref="HotelPhoto">
          <PictureSlider photos={[photo]} imageSize={imageSize} isMobile={isMobile} />
          {this.renderHotelName()}
          {this.renderDistance()}
        </div>
        {this.renderHotelDetails()}
        <div className="HotelRate">
          {hotel.available ? this.renderAvailableRate() : this.renderNotAvailable()}
        </div>
      </div>
    );
  }
}

Hotel.propTypes = {
  hotel: PropTypes.object.isRequired,
  info: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired, // Prop para `navigate`
};

Hotel.contextTypes = {
  t: PropTypes.func.isRequired,
};

// Conectar el componente con Redux
const mapStateToProps = (state) => ({
  lang: state.i18nState.lang,
  currency: state.booking.filterCurrency,
  isMobile: state.booking.device === DEVICES.MOBILE,
});

const ConnectedHotel = connect(mapStateToProps)(Hotel);

// Exportar el componente envuelto con el HOC
export default withNavigate(ConnectedHotel);
